import { useContext } from '@nuxtjs/composition-api';
import type { ApolloQueryResult, FetchResult } from '@apollo/client/core';
import { Logger } from '~/helpers/logger';
import DOMPurify from 'isomorphic-dompurify';
import { mapValues, isArray } from "lodash-es";
import SEND_HYDROGEN_LEADS_REQUEST from '~/extensions/magento-api/sendHydrogenLeadsRequest/sendHydrogenLeadsRequest.gql';
import SEND_HYDROGEN_LEADS_REQUEST_WITHOUT_PRODUCTS from '~/extensions/magento-api/sendHydrogenLeadsRequest/sendHydrogenLeadsRequestWithoutProducts.gql';

export type ContactUsMutationResult = {
  contactUs?: {
    status?: boolean;
  };
};

export type SendHydrogenLeadsRequestMutationResult = {
  sendHydrogenLeadsRequest?: {
    message?: string;
    success?: boolean;
  };
};

export type SendHydrogenLeadsNewsletterRequestMutationResult = {
  sendHydrogenLeadsNewsletterRequest?: {
    message?: string;
    status?: boolean;
    success?: boolean;
  };
};

export type ContactIndustry = {
  industry_id: number;
  name: string;
};

export type ContactIndustryQueryResult = {
  getContactIndustry?: {
    count?: number;
    data?: ContactIndustry[];
  };
};

export const useContact = () => {
  const context = useContext();

  const submitComment = async (variables: object) => {
    const mutationVariables = mapValues(variables, (value) => isArray(value)? value:DOMPurify.sanitize(value));
    
    Logger.debug('[Magento]: Submit Contact Us Form', { mutationVariables });

    const data = await context.app.$vsf.$magento.api.contactUs({ mutationVariables }) as FetchResult<ContactUsMutationResult>;

    Logger.debug('[Result]:', { data });

    return data;
  };

  const submitHydrogenLead = async (variables: object) => {
    const { recaptchaToken, ...leadVariables } = variables as Record<string, unknown>;
    const hasProducts = isArray(leadVariables.products) && leadVariables.products.length > 0;
    const hasNewsletter = typeof leadVariables.newsletter === 'boolean';
    const mutationVariables = mapValues(leadVariables, (value) => {
      if (isArray(value) || typeof value === 'boolean') {
        return value;
      }

      return DOMPurify.sanitize(String(value ?? ''));
    });
    const mutation = (hasProducts ? SEND_HYDROGEN_LEADS_REQUEST : SEND_HYDROGEN_LEADS_REQUEST_WITHOUT_PRODUCTS)
      .replace(hasNewsletter ? '' : /\n  \$newsletter: Boolean/g, '')
      .replace(hasNewsletter ? '' : /\n      newsletter: \$newsletter/g, '');

    Logger.debug('[Magento]: Submit Hydrogen Leads Request', { mutationVariables });

    const data = await (context.app.$vsf.$magento.api as any).customMutation({
      mutation,
      mutationVariables,
      customHeaders: {
        store: 'default',
        ...(recaptchaToken ? { "X-ReCaptcha": recaptchaToken } : {}),
      },
    }) as FetchResult<SendHydrogenLeadsRequestMutationResult>;

    Logger.debug('[Result]:', { data });

    return data;
  };

  const submitHydrogenLeadsNewsletterRequest = async (email: string) => {
    const mutationVariables = {
      email: DOMPurify.sanitize(email),
    };

    Logger.debug('[Magento]: Submit Hydrogen Leads Newsletter Request', { mutationVariables });

    try {
      const data = await (context.app.$vsf.$magento.api as any).sendHydrogenLeadsNewsletterRequest({
        mutationVariables,
      }) as FetchResult<SendHydrogenLeadsNewsletterRequestMutationResult>;

      Logger.debug('[Result]:', { data });

      return data;
    } catch (err) {
      Logger.error('[Magento]: Submit Hydrogen Leads Newsletter Request failed', err);

      return {
        data: null,
        errors: [{
          message: 'Newsletter subscription request timed out. Please try again later.',
        }],
      } as unknown as FetchResult<SendHydrogenLeadsNewsletterRequestMutationResult>;
    }
  };

  const getContactIndustry = async () => {
    Logger.debug('useContact/getContactIndustry');

    const data = await context.app.$vsf.$magento.api.getContactIndustry() as ApolloQueryResult<ContactIndustryQueryResult>;

    Logger.debug('[Result]:', { data });

    return data?.data;
  };

  return {
    submitComment,
    submitHydrogenLead,
    submitHydrogenLeadsNewsletterRequest,
    getContactIndustry,
  };
};

export default useContact;
